/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import type { LearnerProfile_WorkHistory as WorkHistory } from '__generated__/graphql-types';

import { Divider, ShowMoreList } from '@coursera/cds-core';

import { getOccupationIdOrName } from 'bundles/account-profile/common/utils';
import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import EmptyWorkHistory from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-history/EmptyWorkHistory';
import {
  WorkHistoryItem,
  WorkHistoryItemPlaceHolder,
} from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-history/WorkHistoryItem';

import _t from 'i18n!nls/account-profile';

type Props = {
  handleOpenWorkHistoryModal: (workHistoryItem?: WorkHistory) => void;
};

const styles = {
  divider: () => css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
};

const DEFAULT_ITEMS_TO_SHOW = 3;

const WorkHistoryBody: React.VFC<Props> = ({ handleOpenWorkHistoryModal }) => {
  const { isProfileLoading, profileState = {}, editable } = useContext(ProfileContext);
  const { workHistory = [] } = profileState;
  const { isRolesMapLoading } = useContext(OccupationRolesContext);

  // I make a copy of the array to avoid sorting in place which sometimes throws an error
  const sortedWorkHistory = [...workHistory].sort((w1, w2) => {
    if (w1.isCurrentlyEmployed) {
      return -1;
    } else if (w2.isCurrentlyEmployed) {
      return 1;
    } else if ((w1.endDate?.year ?? 0) >= (w2.endDate?.year ?? 0)) {
      return -1;
    } else {
      return 1;
    }
  });

  if (isProfileLoading || isRolesMapLoading) {
    return (
      <div>
        <WorkHistoryItemPlaceHolder />
        <Divider css={styles.divider} />
        <WorkHistoryItemPlaceHolder />
        <Divider css={styles.divider} />
        <WorkHistoryItemPlaceHolder />
      </div>
    );
  } else if (editable && !workHistory.length) {
    return <EmptyWorkHistory handleOpenWorkHistoryModal={handleOpenWorkHistoryModal} />;
  } else if (workHistory.length <= DEFAULT_ITEMS_TO_SHOW) {
    return (
      <div data-testid="all-work-items">
        {sortedWorkHistory.map((workItem, idx) => (
          <WorkHistoryItem
            workHistoryItem={workItem}
            key={`${workItem.employerName}~${getOccupationIdOrName(workItem.jobTitle)}`}
            hasTopBorder={idx !== 0}
            handleOpenWorkHistoryModal={handleOpenWorkHistoryModal}
          />
        ))}
      </div>
    );
  } else {
    return (
      <ShowMoreList
        numItemsToShow={DEFAULT_ITEMS_TO_SHOW}
        controllerProps={{ itemTypePlural: _t('credentials'), ariaLabel: _t('Show all work experiences') }}
      >
        {sortedWorkHistory.map((workItem, idx) => (
          <WorkHistoryItem
            workHistoryItem={workItem}
            key={`${workItem.employerName}~${getOccupationIdOrName(workItem.jobTitle)}`}
            hasTopBorder={idx !== 0}
            handleOpenWorkHistoryModal={handleOpenWorkHistoryModal}
          />
        ))}
      </ShowMoreList>
    );
  }
};

export default WorkHistoryBody;
